import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControllableObjectMapItem,
  ControllableObjectMessageType,
  ControllableObjectType,
  ControllableObjectUpdateData,
  isLocalizedString,
  Item,
  LocalizedString,
  MonitorItem,
  NavigationGeodataObject,
  ProjectObjectCollapsedEnum,
  ProjectObjectSizeEnum,
  ShowInMapOrDetailModeEnum,
  ShowOnFloorplanEnum,
  SignalRService,
} from '@cue/api';
import {
  DeclarativeMapCanvasComponent,
  DeclarativeMapMarkerComponent,
  DeclarativeMapMarkerTemplateDirective,
} from '@cue/map/elements';
import { NgStyle } from '@angular/common';
import {
  cueArrowUp,
  cueCheckCircle,
  cueExclamation,
  cueIcon,
  cueInfo,
  cueMinus,
  cuePlus,
  cueWarning,
  IconComponent,
} from '@cue/ui/icons';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { localized } from '@cue/translate';
import { CmUnityLocalizedStringPipe } from '@cue/utilities';
import {
  ButtonComponent,
  KendoSliderDirectionDirective,
  NumericSpinnerChangeDirective,
  RepeatPressDirective,
} from '@cue/ui';
import { RepeatButtonIntervalData } from '@cue/data';
import { takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { FormsModule } from '@angular/forms';
import { TimepickerComponent } from '@assist/ui/dateinputs';
import { NumericTextBoxModule, SliderComponent } from '@progress/kendo-angular-inputs';
import { Memoize } from 'typescript-memoize';
import { TooltipDirective } from '@cue/ui/tooltips';
import { MAP_ACTIVE_Z_INDEX_OFFSET } from '@cue/map/core';

export enum MarginFromParentPOI {
  None,
  Normal,
  Large,
}

// export enum PaddingFromParentPOI {
//   Default,
//   None,
//   Small,
//   Medium,
//   Large,
// }

@Component({
  selector: 'cue-map-unit-object',
  imports: [
    DeclarativeMapMarkerComponent,
    NgStyle,
    DeclarativeMapMarkerTemplateDirective,
    IconComponent,
    CmUnityLocalizedStringPipe,
    ButtonComponent,
    TranslocoPipe,
    FormsModule,
    TimepickerComponent,
    NumericTextBoxModule,
    SliderComponent,
    TooltipDirective,
    RepeatPressDirective,
    NumericSpinnerChangeDirective,
    KendoSliderDirectionDirective,
  ],
  templateUrl: './map-unit-object.component.html',
  styleUrl: './map-unit-object.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class MapUnitObjectComponent implements OnInit, OnDestroy {
  protected readonly activeZIndexOffset = MAP_ACTIVE_Z_INDEX_OFFSET;
  private readonly legacyLongNumberInputType = 'lonf-number-input-1';
  private readonly sliderActionThrottleMs = 80;
  ICONS = {
    cueArrowUp,
    cueInfo,
    cueMinus,
    cuePlus,
  };
  click = output<void>();
  signalRService = inject(SignalRService);
  canvas = inject(DeclarativeMapCanvasComponent);
  private pendingSliderActionTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  private lastSliderActionSentAt = new Map<string, number>();
  private readonly invalidNumberInputs = signal<ReadonlySet<string>>(new Set());

  terminator = injectDestroy();

  ngOnDestroy(): void {
    this.pendingSliderActionTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.pendingSliderActionTimeouts.clear();
    this.lastSliderActionSentAt.clear();
  }

  private updateData(updatedData: ControllableObjectUpdateData) {
    if (this.unitObject()?.objectsData) {
      let updated = false;
      const found = this.unitObject()?.objectsData.find((x) => x.objectId === updatedData.objectId);
      if (found) {
        this.unitObject.update((uO) => {
          const temp = {
            ...uO,
            objectsData: uO.objectsData.map((obj) => {
              if (obj.objectId !== updatedData.objectId) {
                return obj;
              }

              //nema urceno ID - updatuje se pouze label hlavniho objektu
              if (updatedData.data?.id == null && updatedData.data?.label) {
                console.log(updatedData);
                updated = true;
                return {
                  ...obj,
                  data: {
                    ...obj.data,
                    label: updatedData.data.label,
                  },
                };
              }

              const foundItem = obj.data.item?.find((i) => i.id === updatedData.data.id);
              if (foundItem) {
                updated = true;
                return {
                  ...obj,
                  data: {
                    ...obj.data,
                    group: obj.data.group?.map((g) => {
                      return g;
                    }),
                    item: obj.data.item.map((i) => {
                      if (i.id === updatedData.data.id) {
                        return {
                          ...i,
                          ...updatedData.data,
                        };
                      } else {
                        return i;
                      }
                    }),
                  },
                };
              } else {
                const foundGroup = obj.data.group?.find((x) => x.item.some((i) => i.id === updatedData.data.id));
                if (foundGroup) {
                  updated = true;
                  return {
                    ...obj,
                    data: {
                      ...obj.data,
                      group: obj.data.group.map((g) => {
                        const foundItem = g.item.find((i) => i.id === updatedData.data.id);
                        if (foundItem) {
                          return {
                            ...g,
                            item: g.item.map((i) => {
                              if (i.id === updatedData.data.id) {
                                return {
                                  ...i,
                                  ...updatedData.data,
                                };
                              } else {
                                return i;
                              }
                            }),
                          };
                        } else {
                          return g;
                        }
                      }),
                    },
                  };
                } else if (updatedData.data.label) {
                  updated = true;
                  return {
                    ...obj,
                    data: {
                      ...obj.data,
                      group: obj.data.group.map((x) => {
                        if (x.id === updatedData.data?.id) {
                          return {
                            ...x,
                            label: updatedData.data.label,
                          };
                        } else {
                          return x;
                        }
                      }),
                    },
                  };
                } else {
                  return {
                    ...obj,
                    data: {
                      ...obj.data,
                      group: obj.data.group?.map((g) => {
                        if (g.message.id === updatedData.data.id) {
                          updated = true;
                          return {
                            ...g,
                            message: {
                              ...g.message,
                              ...updatedData.data,
                            },
                          };
                        } else {
                          return g;
                        }
                      }),
                    },
                  };
                }
              }
            }),
          };

          if (updated) {
            return temp;
          }

          if (!updated) {
            let temp2: any = null;
            if (this.isIndicatorType(found.data.type)) {
              temp2 = {
                ...uO,
                objectsData: uO.objectsData.map((obj) => {
                  if (obj.objectId !== updatedData.objectId) {
                    return obj;
                  }

                  updated = true;

                  return {
                    ...obj,
                    data: {
                      ...obj.data,
                      ...updatedData.data,
                    },
                  };
                }),
              };
            }
            if (updated) {
              return temp2;
            }
            return {
              ...uO,
              objectsData: uO.objectsData.map((obj) => {
                if (obj.objectId !== updatedData.objectId) {
                  return obj;
                }

                updated = true;

                return {
                  ...obj,
                  data: {
                    ...obj.data,
                    message: {
                      ...obj.data.message,
                      ...updatedData.data,
                    },
                  },
                };
              }),
            };
          }
        });
      }
    }
  }

  ngOnInit() {
    this.signalRService
      .listen<ControllableObjectUpdateData>('objectChanged', false, '/MessageHub')
      .pipe(takeUntil(this.terminator))
      .subscribe((updatedData) => {
        if (!updatedData) {
          return;
        }
        this.updateData(updatedData);
      });
    // this.signalRService.controllableObjectUpdateData$.pipe(takeUntil(this.terminator)).subscribe((updatedData) => {
    //   if (!updatedData) {
    //     return;
    //   }
    //   this.updateData(updatedData);
    // });
  }

  // ma v sobe poiSize, zoomPoi, objectsData, nic jineho neni v podstate potreba
  unitObject = model<NavigationGeodataObject>();

  _startedIntervalIdForItem: RepeatButtonIntervalData[] = [];
  zoom = computed(() => {
    return this.canvas.zoom();
  });

  marker = viewChild(DeclarativeMapMarkerComponent);
  focused = input<boolean>(false);
  focusMarkerOnFocused = input<boolean>(false);

  marginFromParentPOI = computed(() => {
    if (this.focused()) {
      switch (this.unitObject()?.showOnFloorplanDetail) {
        case ShowOnFloorplanEnum.None:
          return MarginFromParentPOI.None;
        case ShowOnFloorplanEnum.OnlyCategoryIcon:
        case ShowOnFloorplanEnum.OnlyImage:
        case ShowOnFloorplanEnum.OnlyName:
        case ShowOnFloorplanEnum.OnlyCode:
          return MarginFromParentPOI.Normal;
        case ShowOnFloorplanEnum.CategoryIconAndName:
        case ShowOnFloorplanEnum.CategoryIconAndImage:
        case ShowOnFloorplanEnum.CategoryIconAndCode:
        case ShowOnFloorplanEnum.CodeAndName:
          return MarginFromParentPOI.Normal;
        case ShowOnFloorplanEnum.ImageAndName:
          return MarginFromParentPOI.Large;
        default:
          return MarginFromParentPOI.Normal;
      }
    } else {
      switch (this.unitObject().showOnFloorplan) {
        case ShowOnFloorplanEnum.None:
          return MarginFromParentPOI.None;
        case ShowOnFloorplanEnum.OnlyCategoryIcon:
        case ShowOnFloorplanEnum.OnlyImage:
        case ShowOnFloorplanEnum.OnlyName:
        case ShowOnFloorplanEnum.OnlyCode:
          return MarginFromParentPOI.Normal;
        case ShowOnFloorplanEnum.CategoryIconAndName:
        case ShowOnFloorplanEnum.CategoryIconAndImage:
        case ShowOnFloorplanEnum.CategoryIconAndCode:
        case ShowOnFloorplanEnum.CodeAndName:
          return MarginFromParentPOI.Normal;
        case ShowOnFloorplanEnum.ImageAndName:
          return MarginFromParentPOI.Large;
        default:
          return MarginFromParentPOI.Normal;
      }
    }
  });

  // jelikoz nikdo v cele firme neni schopen specifikovat,
  // jak se pozna jen tecka - tak aby se narozdil od vseho ostatniho "neposouvala", tak to proste zkusime jen uhadnout
  // pro me naprtosto nepochopitelna vec... ale budiz
  centerObjectToPOI = computed(() => {
    return this.marginFromParentPOI() === MarginFromParentPOI.None;
  });

  marginFromPOIPixels = computed(() => {
    switch (this.marginFromParentPOI()) {
      case MarginFromParentPOI.None:
        return 0;
      case MarginFromParentPOI.Normal:
        return 5;
      case MarginFromParentPOI.Large:
        return 10;
      default:
        return 15;
    }
  });

  isOnlyOneIndicator = computed(() => {
    return this.showableItemList().length === 1 && this.isIndicatorType(this.showableItemList()[0].data.type);
  });

  getIntoViewWhenFocused = explicitEffect(
    [this.focused, this.focusMarkerOnFocused],
    ([focused, focusMarkerOnFocused]) => {
      if (focused && focusMarkerOnFocused) {
        this.marker()?.focus(this.isOnlyOneIndicator() ? undefined : 'top-center');
      }
    }
  );

  openTabsOpenOverride = signal<Record<number, boolean>>({});

  private canShowHideableItemProperty(item: ControllableObjectMapItem, property: ShowInMapOrDetailModeEnum): boolean {
    return item.showInMap?.some((x) => x == property);
  }

  private canShowControl(item: ControllableObjectMapItem): boolean {
    return (
      (this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Label) && item.data.label != null) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Control)
    );
  }

  private canShowValueInput(item: ControllableObjectMapItem): boolean {
    return (
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.ObjectLabel) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Label) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Control) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Value) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Unit) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Limits)
    );
  }

  private canShowMonitor(item: ControllableObjectMapItem): boolean {
    return (
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.ObjectLabel) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Icon) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Label) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Value) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Unit) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Color)
    );
  }

  private convertRgbaToRgb(alpha: number) {
    const color = this.unitObject().qrCategoryColor;
    if (!color || color?.startsWith('rgb(')) {
      return color;
    }
    // Extract first 3 numbers from rgba
    const [r, g, b] = color.match(/\d+/g).map(Number);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private canShowIndicator(item: ControllableObjectMapItem): boolean {
    return (
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Label) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Icon) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Color) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Value)
    );
  }

  private getTextColorBasedOnBg(color?: string): string {
    if (!color) return '#ffffff';
    const [r, g, b] = color.match(/\d+/g).map(Number);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 128 ? '#212529' : '#ffffff';
  }

  private canShowItem(item: ControllableObjectMapItem): boolean {
    if (!item?.data) {
      return false;
    }

    switch (item.data.type) {
      case ControllableObjectType.Control:
        return this.canShowControl(item);
      case ControllableObjectType.Slider:
      case ControllableObjectType.TimeInput:
      case ControllableObjectType.DoubleNumberInput:
      case ControllableObjectType.LongNumberInput:
        return this.canShowValueInput(item);
      case ControllableObjectType.Monitor:
        return this.canShowMonitor(item);
      case ControllableObjectType.Indicator:
        return this.canShowIndicator(item);
      default:
        return false;
    }
  }

  private isOneItemWrapper(controllableItem: ControllableObjectMapItem): boolean {
    if (this.isValueInputType(controllableItem.data?.type)) {
      return (controllableItem.data?.item?.length ?? 0) <= 1 && !controllableItem.data?.label;
    }

    const group = controllableItem.data?.group;
    const hasSingleGroup = group?.length === 1;
    const singleGroupHasMaxTwoButtons = hasSingleGroup && group[0].item.length <= 2;
    const singleGroupNoLabelNoMessage = hasSingleGroup && !group[0].label && !group[0].message?.text;
    const noLabelAndMessage = !controllableItem.data.label && !controllableItem.data.message?.text && hasSingleGroup;
    return noLabelAndMessage && singleGroupNoLabelNoMessage && singleGroupHasMaxTwoButtons;
  }

  protected isOneColumnWrapper(controllableItem: ControllableObjectMapItem): boolean {
    if (this.isValueInputType(controllableItem.data?.type)) {
      return true;
    }

    const group = controllableItem.data?.group;
    return group?.every((groupItem) => groupItem.item.length === 1);
  }

  private foldingEnabled(controllableItem: ControllableObjectMapItem) {
    if (this.isMonitorType(controllableItem.data.type) && controllableItem.data.item?.length === 1) {
      return false;
    }
    if (
      controllableItem.projectObjectCollapseId === ProjectObjectCollapsedEnum.CollapsedLocked ||
      controllableItem.projectObjectCollapseId === ProjectObjectCollapsedEnum.ExpandedLocked
    ) {
      return false;
    }
    return true;
  }

  private isOpen(controllableItem: ControllableObjectMapItem) {
    const foundInOverride = this.openTabsOpenOverride()[controllableItem.objectId];
    if (foundInOverride !== undefined) {
      return foundInOverride;
    }

    if (controllableItem.projectObjectCollapseId === ProjectObjectCollapsedEnum.ExpandedDefault) {
      return true;
    }

    if (controllableItem.projectObjectCollapseId === ProjectObjectCollapsedEnum.CollapsedDefault) {
      return false;
    }

    if (controllableItem.projectObjectCollapseId === ProjectObjectCollapsedEnum.CollapsedLocked) {
      return false;
    }
    if (controllableItem.projectObjectCollapseId === ProjectObjectCollapsedEnum.ExpandedLocked) {
      return true;
    }
    return true;
  }

  translocoService = inject(TranslocoService);
  currentLanguage = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  private getCmUnityLocalizedString(localizedString: LocalizedString) {
    if (!localizedString) return null;

    if (!isLocalizedString(localizedString)) {
      return localizedString;
    }

    return localized(localizedString, this.translocoService, false) || localizedString['default'];
  }

  private labelEnabled(controllableItem: ControllableObjectMapItem, language: string) {
    // Keep the language!
    return (
      this.canShowHideableItemProperty(controllableItem, ShowInMapOrDetailModeEnum.Label) &&
      this.getCmUnityLocalizedString(controllableItem.data.label)
    );
  }

  private isControlType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Control;
  }

  protected isValueInputType(type?: ControllableObjectType | string): boolean {
    return this.isSliderType(type) || this.isTimeInputType(type) || this.isNumberInputType(type);
  }

  protected isSliderType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Slider;
  }

  protected isTimeInputType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.TimeInput;
  }

  protected isDoubleNumberInputType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.DoubleNumberInput;
  }

  protected isNumberInputType(type?: ControllableObjectType | string): boolean {
    return (
      type === ControllableObjectType.LongNumberInput ||
      type === this.legacyLongNumberInputType ||
      this.isDoubleNumberInputType(type)
    );
  }

  private isMonitorType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Monitor;
  }

  private isIndicatorType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Indicator;
  }

  protected canShowValueInputObjectLabel(item: ControllableObjectMapItem): boolean {
    return !!item?.data?.label && this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.ObjectLabel);
  }

  protected hasInputValue(item?: MonitorItem): boolean {
    return item?.value !== null && item?.value !== undefined && item?.value !== '';
  }

  protected hasInputLimits(item?: MonitorItem): boolean {
    return item?.minimum !== undefined || item?.maximum !== undefined;
  }

  protected getInputLimitsText(item?: MonitorItem): string {
    if (!item) {
      return '';
    }

    if (item.minimum !== undefined && item.maximum !== undefined) {
      return `${item.minimum}–${item.maximum}`;
    }

    if (item.minimum !== undefined) {
      return `≥ ${item.minimum}`;
    }

    if (item.maximum !== undefined) {
      return `≤ ${item.maximum}`;
    }

    return '';
  }

  protected getInputValue(item?: MonitorItem): string | number {
    return (item?.value as string | number) ?? '';
  }

  protected getSliderValue(item?: MonitorItem): number {
    const value = Number(item?.value ?? item?.minimum ?? 0);
    return Number.isFinite(value) ? value : 0;
  }

  protected getSliderMin(item?: MonitorItem): number {
    const min = Number(item?.minimum ?? 0);
    return Number.isFinite(min) ? min : 0;
  }

  protected getSliderMax(item?: MonitorItem): number {
    const max = Number(item?.maximum ?? 100);
    return Number.isFinite(max) ? max : 100;
  }

  protected getSliderStep(item?: MonitorItem): number {
    const step = Number(item?.step ?? 1);
    return Number.isFinite(step) && step > 0 ? step : 1;
  }

  protected isSliderReversed(item?: MonitorItem): boolean {
    return this.getSliderMin(item) > this.getSliderMax(item);
  }

  protected getSliderLowerBound(item?: MonitorItem): number {
    return Math.min(this.getSliderMin(item), this.getSliderMax(item));
  }

  protected getSliderUpperBound(item?: MonitorItem): number {
    return Math.max(this.getSliderMin(item), this.getSliderMax(item));
  }

  protected getSliderMinStepDirection(item?: MonitorItem): -1 | 1 {
    return this.isSliderReversed(item) ? 1 : -1;
  }

  protected getSliderMaxStepDirection(item?: MonitorItem): -1 | 1 {
    return this.isSliderReversed(item) ? -1 : 1;
  }

  protected isSliderAtMin(item?: MonitorItem): boolean {
    const value = this.getSliderValue(item);
    const min = this.getSliderMin(item);
    return this.isSliderReversed(item) ? value >= min : value <= min;
  }

  protected isSliderAtMax(item?: MonitorItem): boolean {
    const value = this.getSliderValue(item);
    const max = this.getSliderMax(item);
    return this.isSliderReversed(item) ? value <= max : value >= max;
  }

  protected stepSliderValue(objectId: string, item: MonitorItem, direction: -1 | 1): void {
    const lowerBound = this.getSliderLowerBound(item);
    const upperBound = this.getSliderUpperBound(item);
    const current = this.getSliderValue(item);
    const next = Math.min(upperBound, Math.max(lowerBound, current + direction * this.getSliderStep(item)));
    if (next === current) {
      return;
    }

    this.onSliderValueChanged(objectId, item.id, next);
  }

  protected setSliderToMin(objectId: string, item: MonitorItem): void {
    if (this.isSliderAtMin(item)) {
      return;
    }
    this.onSliderValueChanged(objectId, item.id, this.getSliderMin(item));
  }

  protected setSliderToMax(objectId: string, item: MonitorItem): void {
    if (this.isSliderAtMax(item)) {
      return;
    }
    this.onSliderValueChanged(objectId, item.id, this.getSliderMax(item));
  }

  protected onSliderValueChanged(objectId: string, itemId: string, value: number | null): void {
    this.onValueInputChanged(objectId, itemId, `${value ?? 0}`, ControllableObjectType.Slider);
    this.scheduleSliderValueInputAction(objectId, itemId);
  }

  protected onNumberValueChanged(
    objectId: string,
    itemId: string,
    value: number | null,
    type?: ControllableObjectType | string
  ): void {
    const rawValue = value == null ? '' : `${value}`;
    const normalizedValue = this.normalizeValueInputValue(rawValue, type);
    const currentValue = this.getValueInputActionValue(objectId, itemId);
    const normalizedCurrentValue = this.normalizeValueInputValue(currentValue == null ? '' : `${currentValue}`, type);

    if (normalizedValue === normalizedCurrentValue) {
      return;
    }

    this.onValueInputChanged(objectId, itemId, rawValue, type);
    this.performValueInputAction(objectId, itemId);
  }

  protected commitNumberValue(
    objectId: string,
    item: MonitorItem,
    value: number | null,
    type?: ControllableObjectType | string
  ): void {
    const inputKey = this.getNumberInputKey(objectId, item.id);
    const isOutsideRange = this.isNumberValueOutsideRange(item, value);
    this.setNumberInputRangeError(inputKey, isOutsideRange);
    if (isOutsideRange) {
      return;
    }

    this.onNumberValueChanged(objectId, item.id, value, type);
  }

  protected hasNumberInputRangeError(objectId: string, itemId: string): boolean {
    return this.invalidNumberInputs().has(this.getNumberInputKey(objectId, itemId));
  }

  private isNumberValueOutsideRange(item: MonitorItem, value: number | null): boolean {
    if (value == null) {
      return false;
    }

    return (
      !Number.isFinite(value) ||
      (Number.isFinite(item.minimum) && value < item.minimum) ||
      (Number.isFinite(item.maximum) && value > item.maximum)
    );
  }

  private setNumberInputRangeError(inputKey: string, hasError: boolean): void {
    this.invalidNumberInputs.update((currentErrors) => {
      if (currentErrors.has(inputKey) === hasError) {
        return currentErrors;
      }

      const nextErrors = new Set(currentErrors);
      if (hasError) {
        nextErrors.add(inputKey);
      } else {
        nextErrors.delete(inputKey);
      }
      return nextErrors;
    });
  }

  private getNumberInputKey(objectId: string, itemId: string): string {
    return JSON.stringify([objectId, itemId]);
  }

  protected stopEditableInputEvent(event: Event): void {
    event.stopPropagation();
  }

  protected onValueInputChanged(
    objectId: string,
    itemId: string,
    rawValue: string,
    type?: ControllableObjectType | string
  ): void {
    const value = this.normalizeValueInputValue(rawValue, type);

    this.unitObject.update((currentObject) => ({
      ...currentObject,
      objectsData: currentObject.objectsData.map((controllableItem) =>
        controllableItem.objectId === objectId
          ? {
              ...controllableItem,
              data: {
                ...controllableItem.data,
                item: (controllableItem.data.item ?? []).map((item) =>
                  item.id === itemId
                    ? {
                        ...item,
                        value,
                      }
                    : item
                ),
              },
            }
          : controllableItem
      ),
    }));
  }

  @Memoize((item?: MonitorItem) => `${item?.id ?? ''};${item?.value ?? ''}`)
  protected getTimePickerValue(item?: MonitorItem): Date | null {
    const value = item?.value;
    if (typeof value !== 'string') {
      return null;
    }

    const [hours, minutes] = value.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  protected commitTimePickerValue(objectId: string, itemId: string, value: Date | null): void {
    const formattedValue = this.formatTimePickerValue(value);
    const currentValue = this.getValueInputActionValue(objectId, itemId);
    const normalizedCurrentValue = currentValue == null ? '' : `${currentValue}`;

    if (formattedValue === normalizedCurrentValue) {
      return;
    }

    this.onValueInputChanged(objectId, itemId, formattedValue, ControllableObjectType.TimeInput);
    this.performValueInputAction(objectId, itemId);
  }

  private normalizeValueInputValue(rawValue: string, type?: ControllableObjectType | string): string | number {
    if (this.isSliderType(type)) {
      const parsed = Number(rawValue);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    if (this.isNumberInputType(type)) {
      if (rawValue === '') {
        return '';
      }

      const parsed = Number(rawValue);
      return Number.isFinite(parsed) ? (this.isDoubleNumberInputType(type) ? parsed : Math.trunc(parsed)) : rawValue;
    }

    return rawValue;
  }

  private formatTimePickerValue(value: Date | null): string {
    if (!value) {
      return '';
    }

    const hours = `${value.getHours()}`.padStart(2, '0');
    const minutes = `${value.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private getValueInputActionValue(objectId: string, itemId: string): string | number | undefined {
    const value = this.unitObject()
      .objectsData.find((controllableItem) => controllableItem.objectId === objectId)
      ?.data.item?.find((item) => item.id === itemId)?.value;

    return typeof value === 'string' || typeof value === 'number' ? value : undefined;
  }

  private performValueInputAction(objectId: string, itemId: string): void {
    this.signalRService.sendValueMessageSignalR(
      objectId,
      itemId,
      this.getValueInputActionValue(objectId, itemId),
      '/MessageHub'
    );
  }

  private scheduleSliderValueInputAction(objectId: string, itemId: string): void {
    const actionKey = `${objectId}:${itemId}`;

    if (this.pendingSliderActionTimeouts.has(actionKey)) {
      return;
    }

    const now = Date.now();
    const lastSentAt = this.lastSliderActionSentAt.get(actionKey) ?? 0;
    const elapsed = now - lastSentAt;

    if (elapsed >= this.sliderActionThrottleMs) {
      this.lastSliderActionSentAt.set(actionKey, now);
      this.performValueInputAction(objectId, itemId);
      return;
    }

    const timeoutId = setTimeout(() => {
      this.pendingSliderActionTimeouts.delete(actionKey);
      this.lastSliderActionSentAt.set(actionKey, Date.now());
      this.performValueInputAction(objectId, itemId);
    }, this.sliderActionThrottleMs - elapsed);

    this.pendingSliderActionTimeouts.set(actionKey, timeoutId);
  }

  protected getDefaultIconAccordingStatus(statusType: ControllableObjectMessageType): cueIcon {
    switch (statusType) {
      case ControllableObjectMessageType.Success:
        return cueCheckCircle;
      case ControllableObjectMessageType.Warning:
        return cueWarning;
      case ControllableObjectMessageType.Error:
        return cueExclamation;
      case ControllableObjectMessageType.Info:
        return cueInfo;
      default:
        return null;
    }
  }

  private getTextColorIfAvailable(item: ControllableObjectMapItem): string {
    if (!this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Color)) {
      return '#212529';
    }
    return this.getTextColorBasedOnBg(item.data?.color);
  }

  controllableItemList = computed(() => {
    return this.unitObject()
      .objectsData.sort((a, b) => b.rank - a.rank)
      .map((obj) => {
        const canShowHideableControl = this.canShowHideableItemProperty(obj, ShowInMapOrDetailModeEnum.Control);
        const canShowHideableColor = this.canShowHideableItemProperty(obj, ShowInMapOrDetailModeEnum.Color);
        const canShowHideableIcon = this.canShowHideableItemProperty(obj, ShowInMapOrDetailModeEnum.Icon);
        const canShowHideableValue = this.canShowHideableItemProperty(obj, ShowInMapOrDetailModeEnum.Value);
        const canShowHideableUnit = this.canShowHideableItemProperty(obj, ShowInMapOrDetailModeEnum.Unit);
        const canShowHideableLabel = this.canShowHideableItemProperty(obj, ShowInMapOrDetailModeEnum.Label);
        const canShowHideableObjectLabel = this.canShowHideableItemProperty(obj, ShowInMapOrDetailModeEnum.ObjectLabel);
        const canShowHideableLimits = this.canShowHideableItemProperty(obj, ShowInMapOrDetailModeEnum.Limits);
        return {
          ...obj,
          data: {
            ...obj.data,
            item: (obj.data?.item ?? [])
              .filter((it) => it.iconUrl || it.label || it.unit || this.hasInputValue(it) || this.hasInputLimits(it))
              .map((it) => {
                return {
                  ...it,
                  hasColor: canShowHideableColor && it.color,
                  showIcon: canShowHideableIcon && it.iconUrl,
                  iconBackgroundColor: canShowHideableColor && it.color ? it.color : 'none',
                  automaticIconColor: this.getTextColorBasedOnBg(it.color),
                  showColor: canShowHideableColor && it.color,
                  showValue: this.isMonitorType(obj.data?.type) ? canShowHideableValue : canShowHideableControl,
                  showUnit: canShowHideableUnit && it.unit,
                  showLabel: canShowHideableLabel && it.label,
                  showLimits: canShowHideableLimits,
                };
              }),
          },
          objectScale: this.getObjectScale(obj.projectObjectSizeId),
          hasValue: canShowHideableValue && obj.data.value,
          hasIcon: canShowHideableIcon && obj.data.iconUrl,
          hasLabel: canShowHideableLabel && obj.data.label,
          hasColor: canShowHideableColor && obj.data.color,
          backgroundColor: canShowHideableColor && obj.data.color ? obj.data.color : 'none',
          color: this.getTextColorIfAvailable(obj),
          open: this.isOpen(obj),
          noSpacing: !canShowHideableControl,
          showLabel: this.labelEnabled(obj, this.currentLanguage()),
          showObjectLabel: canShowHideableObjectLabel && !!this.getCmUnityLocalizedString(obj.data.label),
          canShow: this.canShowItem(obj),
          canShowHidableControl: canShowHideableControl,
          oneItem: this.isOneItemWrapper(obj),
          oneColumn: this.isOneColumnWrapper(obj),
          foldingEnabled: this.foldingEnabled(obj),
          showMonitor: this.isMonitorType(obj.data?.type),
          showControl: this.isControlType(obj.data?.type),
          showValueInput: this.isValueInputType(obj.data?.type),
          controlIcon: obj.data.message?.iconUrl ?? this.getDefaultIconAccordingStatus(obj.data?.message?.type),
          showIndicator:
            this.isIndicatorType(obj.data?.type) &&
            (canShowHideableColor || canShowHideableIcon || canShowHideableValue || canShowHideableLabel),
        };
      });
  });

  showableItemList = computed(() => {
    return this.controllableItemList().filter((obj) => obj.canShow);
  });

  hasVisibleValueInput = computed(() => {
    return this.showableItemList().some((obj) => obj.showValueInput);
  });

  customRgb = computed(() => {
    return this.convertRgbaToRgb(1);
  });

  customRgba = computed(() => {
    return this.unitObject().qrCategoryColor;
  });

  customRgbaSaturated = computed(() => {
    return this.convertRgbaToRgb(0.8);
  });

  automaticTextColor = computed(() => {
    return this.getTextColorBasedOnBg(this.unitObject().qrCategoryColor);
  });

  customResizeBasedOnZoomEnabled = computed(() => {
    return this.unitObject()?.zoomPoi;
  });

  sizeCoefficient = computed(() => {
    return this.unitObject()?.poiSize ?? 1;
  });

  private readonly minZoomScaleThreshold = 0.5;

  scaleFactor = computed(() => {
    const zoomValue = Number.isFinite(this.zoom()) ? this.zoom() : 1;
    let nonZoomedScale = 1;
    if (this.customResizeBasedOnZoomEnabled()) {
      if (zoomValue >= this.minZoomScaleThreshold) {
        nonZoomedScale = this.sizeCoefficient() * zoomValue;
      } else {
        nonZoomedScale = this.sizeCoefficient() * this.minZoomScaleThreshold;
      }
    }
    const focusedScale = this.focused() ? 1.25 : 1;
    return nonZoomedScale * focusedScale;
  });

  scale = computed(() => `scale(${this.scaleFactor()})`);

  private readonly hideDetailScaleThreshold = 0.5;

  isZoomedOut = computed(() => {
    return this.scaleFactor() < this.hideDetailScaleThreshold;
  });

  onToggleClicked($event: MouseEvent, controllableItem: any) {
    $event.stopImmediatePropagation();
    this.openTabsOpenOverride.update((state) => ({
      ...state,
      [controllableItem.objectId]: !controllableItem.open,
    }));
  }

  protected repeatButtonEnd(item: Item, event: MouseEvent) {
    event.stopPropagation();
    const itemIdxToDelete = this._startedIntervalIdForItem.findIndex((x) => x.itemId === item.id);
    if (itemIdxToDelete > -1) {
      clearInterval(this._startedIntervalIdForItem[itemIdxToDelete].startedIntervalId);
      this._startedIntervalIdForItem.splice(itemIdxToDelete, 1);
    }
  }

  protected repeatButtonStart(item: Item, objectId: string, event: MouseEvent) {
    this.performAction(item, objectId, event);
    const intervalId = window.setInterval(() => {
      this.performAction(item, objectId, event);
    }, item.repeatButtonInterval * 1000);
    this._startedIntervalIdForItem.push({
      itemId: item.id,
      startedIntervalId: intervalId,
    });
  }

  protected performAction(item: Item, objectId: string, event: MouseEvent) {
    event.stopPropagation();
    this.signalRService.sendMessageSignalR(objectId, item.id, '/MessageHub');
  }

  protected readonly messageTypeEnum = ControllableObjectMessageType;

  private getObjectScale(projectObjectSizeId: ProjectObjectSizeEnum) {
    switch (projectObjectSizeId) {
      case ProjectObjectSizeEnum.Small:
        return 0.65;
      case ProjectObjectSizeEnum.Medium:
        return 1;
      case ProjectObjectSizeEnum.Large:
        return 1.6;
      default:
        return 1;
    }
  }
}
